import { error } from '@ms/yammer-console-logging';
import { openDB } from 'idb';
import type { IDBPDatabase } from 'idb';

interface KeyValueStore {
  readonly key: string;
  readonly value: string;
}

interface DatabaseSchema {
  readonly keyval: KeyValueStore;
}

const dbInstanceCache = new Map<string, Promise<IDBPDatabase<DatabaseSchema>>>();

const getDatabase = (dbName: string): Promise<IDBPDatabase<DatabaseSchema>> => {
  if (!dbInstanceCache.has(dbName)) {
    const dbPromise = openDB<DatabaseSchema>(dbName, 1, {
      upgrade(db) {
        // Create the object store if it doesn't exist
        if (!db.objectStoreNames.contains('keyval')) {
          db.createObjectStore('keyval');
        }
      },
    });
    dbInstanceCache.set(dbName, dbPromise);

    return dbPromise;
  }

  const dbPromise = dbInstanceCache.get(dbName);

  if (!dbPromise) {
    throw new Error(`Database instance not found for ${dbName}`);
  }

  return dbPromise;
};

let currentDbName = 'engage-cache';

export const setDatabaseName = (dbName: string): void => {
  currentDbName = dbName;
};

type GetItem = <T>(key: string) => Promise<T | null>;
export const getItem: GetItem = async <T>(key: string): Promise<T | null> => {
  try {
    const db = await getDatabase(currentDbName);
    const result = await db.get('keyval', key);

    return result ? JSON.parse(result) : null;
  } catch (e) {
    error('Failed to get item from IndexedDB:', e);

    return null;
  }
};

type SetItem = <T>(key: string, item: T) => Promise<void>;
export const setItem: SetItem = async <T>(key: string, item: T): Promise<void> => {
  try {
    const db = await getDatabase(currentDbName);
    await db.put('keyval', JSON.stringify(item), key);
  } catch (e) {
    error('Failed to set item in IndexedDB:', e);
  }
};

type RemoveItem = (key: string) => Promise<void>;
export const removeItem: RemoveItem = async (key: string): Promise<void> => {
  try {
    const db = await getDatabase(currentDbName);
    await db.delete('keyval', key);
  } catch (e) {
    error('Failed to remove item from IndexedDB:', e);
  }
};

type Clear = () => Promise<void>;
export const clear: Clear = async (): Promise<void> => {
  try {
    const db = await getDatabase(currentDbName);
    await db.clear('keyval');
  } catch (e) {
    error('Failed to clear IndexedDB:', e);
  }
};

type GetAllKeys = () => Promise<string[]>;
export const getAllKeys: GetAllKeys = async (): Promise<string[]> => {
  try {
    const db = await getDatabase(currentDbName);
    const keys = await db.getAllKeys('keyval');

    return keys as string[];
  } catch (e) {
    error('Failed to get all keys from IndexedDB:', e);

    return [];
  }
};

type DeleteKeys = (keys: string[]) => Promise<void>;
export const deleteKeys: DeleteKeys = async (keys: string[]): Promise<void> => {
  try {
    const db = await getDatabase(currentDbName);
    const transaction = db.transaction('keyval', 'readwrite');
    const store = transaction.objectStore('keyval');

    await Promise.all(keys.map((key) => store.delete(key)));
    await transaction.done;
  } catch (e) {
    error('Failed to delete keys from IndexedDB:', e);
  }
};
